import React, { useEffect, useState } from "react";
import styles from "./ProjectMonitoring.module.scss";
import CustomHeader from "components/common/CustomHeader/CustomHeader";
import Table from "components/common/Table/Table";
import SelectCustom from "components/common/Select/Select";
import ViewSVG from "components/SVGComponents/ViewSVG";
import FlagSVG from "components/SVGComponents/FlagSVG";
import { useDispatch, useSelector } from "react-redux";
import {
  exportProjects,
  getAllProjects,
  handleProjectFlaggedStatus,
  handleProjectStatus,
} from "reducer/admin/projectMonitoringSlice";
import { downloadFile, formatDateToReadable } from "utils/constants";
import { Controller, useForm } from "react-hook-form";
import Tooltip from "components/common/Tooltip/Tooltip";
import ReportModal from "components/UI/ReportModal/ReportModal";
import {
  debounce,
  flaggedStatusOptions,
  getFlaggedStatusOption,
  getStatusOption,
  selectStyles,
  statusOptions,
} from "../constants";
import ReportReason from "../SharedComponents/ReportReason/ReportReason";
import Modal from "../Modal/Modal";
import FilterModal from "./FilterModal";
import ApplicantsListModal from "./ApplicantsListModal";

const ProjectMonitoring = () => {
  const [pageSelected, setPageSelected] = useState(1);
  const [rowsPerPageValue, setRowsPerPageValue] = useState(10);
  const [selectedRow, setSelectedRow] = useState();
  const [flaggedModal, setFlaggedModal] = useState(false);
  const [reportText, setReportText] = useState("");
  const [reasonModal, setReasonModal] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [applicantModalJobId, setApplicantModalJobId] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const { control } = useForm();
  const dispatch = useDispatch();

  const { projects, totalCount, isProjectsLoading, isExporting, filters } =
    useSelector((state) => state.projects);

  const handleToggleStatus = async (row, message) => {
    try {
      await dispatch(
        handleProjectStatus({
          jobAliasId: row.jobAliasId,
          isActive: !row?.isActive,
          message: message || reportText,
        })
      ).unwrap();
      setFlaggedModal(false);
      setReportText("");
    } catch (error) {
      console.log("error", error);
    }
  };

  const handleFlaggedModal = async (value, row) => {
    if (value === "inActive") {
      setFlaggedModal(true);
    } else {
      handleToggleStatus(row);
    }
  };

  const handleExport = async () => {
    const res = await dispatch(exportProjects()).unwrap();
    downloadFile(res);
  };

  const handleSearch = debounce((value) => {
    setSearchValue(value);
    dispatch(getAllProjects({ pageSelected, search: value, filters }));
  }, 500);

  const updateFlaggedStatus = async (payload) => {
    try {
      await dispatch(handleProjectFlaggedStatus(payload)).unwrap();
      setFlaggedModal(false);
    } catch (error) {
      console.log("error", error);
    }
  };

  const columns = [
    {
      name: "User Name",
      selector: (row) => <Tooltip data={String(row?.companyName || "-")} />,
      width: "15%",
    },
    {
      name: "Job Title",
      selector: (row) => <Tooltip data={String(row?.jobTitle || "-")} />,
      width: "15%",
    },
    {
      name: "Date & Time",
      selector: (row) => (
        <Tooltip
          data={
            Array.isArray(formatDateToReadable(row?.createdAt))
              ? formatDateToReadable(row?.createdAt).join(" ")
              : String(formatDateToReadable(row?.createdAt) || "-")
          }
        />
      ),
      width: "15%",
    },
    {
      name: "Deleted By",
      selector: (row) => row?.flaggedBy || "-",
      width: "14%",
    },
    {
      name: "Status",
      selector: (row) => (
        <Controller
          name={`status-${row.userAliasId}`}
          control={control}
          defaultValue={getStatusOption(row.isActive)}
          render={({ field }) => (
            <SelectCustom
              {...field}
              onChange={(selectedOption) => {
                field.onChange(selectedOption);
                handleFlaggedModal(selectedOption?.value, row);
                setSelectedRow(row);
              }}
              value={getStatusOption(row.isActive)}
              options={statusOptions}
              styles={selectStyles(!row.isActive)}
            />
          )}
        />
      ),
      width: "15%",
    },
    {
      name: "Flag Status",
      selector: (row) => (
        <Controller
          name={`flaggedStatus-${row.userAliasId}`}
          control={control}
          defaultValue={getFlaggedStatusOption(row.flaggedStatus)}
          render={({ field }) => (
            <SelectCustom
              {...field}
              isDisabled={!row.isReported}
              onChange={(selectedOption) => {
                field.onChange(selectedOption);
                updateFlaggedStatus({
                  jobId: row.jobAliasId,
                  flaggedStatus: selectedOption?.value,
                });
              }}
              value={getFlaggedStatusOption(row.flaggedStatus)}
              options={flaggedStatusOptions}
              styles={selectStyles(row.isReported)}
            />
          )}
        />
      ),
      width: "15%",
    },
    {
      name: "Applicants",
      selector: (row) => (
        row.isActive ? (
          <span
            style={{ cursor: "pointer", display: "flex", justifyContent: "center" }}
            onClick={() => setApplicantModalJobId(row?.jobAliasId || row?.jobId)}
          >
            <ViewSVG />
          </span>
        ) : "-"
      ),
      width: "10%",
    },
    {
      name: "Action",
      selector: (row) => (
        <div className={styles.actions}>
          <span>
            <FlagSVG
              height={20}
              width={18}
              fill={row?.isReported ? "#FF6A6A" : "none"}
              color={row?.isReported ? "#FF6A6A" : "#CCCCCC80"}
            />
          </span>
          <span>
            {row?.flaggedReason && (
              <span onClick={() => setReasonModal(row.flaggedReason)}>
                <ViewSVG />
              </span>
            )}
          </span>
        </div>
      ),
      width: "10%",
    },
  ];

  useEffect(() => {
    dispatch(getAllProjects({ pageSelected, search: searchValue, filters }));
  }, [pageSelected, dispatch, filters, searchValue]);

  return (
    <>
      <div className={styles.main}>
        <CustomHeader
          handleSearch={handleSearch}
          isExporting={isExporting}
          handleExport={handleExport}
          openModal={openModal}
          setOpenModal={setOpenModal}
        />
        {/* <Modal>
          <div className={styles.modaldatelabel}>
            <p>date Range</p>
            <p>reset</p>
            <input type="date" />
            <input type="date" />
            <label>Status</label>
            <select>
              <option>Active</option>
            </select>
                        <label>Flag Staus</label>

            <select>
              <option>Active</option>
            </select>
          </div>
        </Modal> */}
        {openModal && (
          <FilterModal
            onClose={() => setOpenModal(false)}
            searchTerm={searchValue}
          />
        )}
        <Table
          columns={columns}
          data={Array.isArray(projects) ? projects : []}
          className={styles.candidatesTable}
          progressPending={isProjectsLoading}
          paginationProps={{
            isPagination: true,
            tableName: "Project Monitoring",
            pageSelected,
            totalCount: totalCount || 0,
            rowsPerPageValue,
            setRowsPerPageValue,
            setPageSelected,
          }}
        />
        {flaggedModal && (
          <ReportModal
            reportModal={flaggedModal}
            setReportModal={setFlaggedModal}
            coverImage={false}
            type="project"
            handleSubmit={(reason) => handleToggleStatus(selectedRow, reason)}
            setReport={setReportText}
          />
        )}
        {applicantModalJobId && (
          <ApplicantsListModal
            jobId={applicantModalJobId}
            onClose={() => setApplicantModalJobId(null)}
          />
        )}
      </div>
      {reasonModal && (
        <ReportReason reason={reasonModal} setReasonModal={setReasonModal} />
      )}
    </>
  );
};

export default ProjectMonitoring;
